/**
 * Credential Document Generator
 * Creates formatted documents with student login credentials
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface SchoolSettings {
  schoolName: string;
  schoolLogo?: string;
  schoolAddress?: string;
  schoolPhone?: string;
  schoolEmail?: string;
  useCustomHeader?: boolean; // Default false - plain header for letterhead
}

interface StudentCredential {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  gradeName?: string; // Changed from className to gradeName
}

interface CredentialDocumentOptions {
  student: StudentCredential;
  teacherName: string;
  schoolSettings: SchoolSettings;
}

// Path to the static QR code image that links to App Store
// In dev mode with base: '/admin/', we need to use the full path
const QR_CODE_IMAGE_PATH = import.meta.env.DEV ? '/app-store-qr.png' : '/admin/app-store-qr.png';

/**
 * Generate HTML content for credential document
 */
export const generateCredentialHTML = async (options: CredentialDocumentOptions): Promise<string> => {
  const { student, teacherName, schoolSettings } = options;
  const useCustomHeader = schoolSettings.useCustomHeader ?? false;
  
  // Plain header (for school letterhead) or custom header
  const headerHTML = useCustomHeader ? `
    <div class="header custom-header">
      ${schoolSettings.schoolLogo ? `<img src="${schoolSettings.schoolLogo}" alt="${schoolSettings.schoolName}" class="logo">` : ''}
      <div class="school-name">${schoolSettings.schoolName}</div>
      ${schoolSettings.schoolAddress || schoolSettings.schoolPhone || schoolSettings.schoolEmail ? `
        <div class="school-details">
          ${schoolSettings.schoolAddress ? `${schoolSettings.schoolAddress} | ` : ''}
          ${schoolSettings.schoolPhone ? `${schoolSettings.schoolPhone} | ` : ''}
          ${schoolSettings.schoolEmail || ''}
        </div>
      ` : ''}
    </div>
  ` : `
    <div class="header plain-header">
      <!-- Space for school letterhead -->
    </div>
  `;
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>SpellTales Login Credentials - ${student.firstName} ${student.lastName}</title>
  <style>
    @page {
      size: A4;
      margin: 1.5cm;
    }
    
    body {
      font-family: 'Arial', 'Helvetica', sans-serif;
      line-height: 1.6;
      color: #000000;
      max-width: 800px;
      margin: 0 auto;
      padding: 0;
      font-size: 14pt;
    }
    
    .header {
      margin-bottom: 20px;
    }
    
    .plain-header {
      /* Reserve space for school letterhead */
      height: 4cm;
      border-bottom: none;
    }
    
    .custom-header {
      text-align: center;
      border-bottom: 2px solid #000000;
      padding-bottom: 10px;
      margin-bottom: 15px;
    }
    
    .logo {
      max-width: 150px;
      max-height: 60px;
      margin-bottom: 8px;
    }
    
    .school-name {
      font-size: 16pt;
      font-weight: bold;
      color: #000000;
      margin: 8px 0;
    }
    
    .school-details {
      font-size: 10pt;
      color: #333333;
    }
    
    h1 {
      font-size: 16pt;
      font-weight: bold;
      margin: 0 0 15px 0;
      text-align: center;
    }
    
    .intro {
      margin: 15px 0;
      font-size: 11pt;
      line-height: 1.5;
    }
    
    .credentials-box {
      border: 2px solid #000000;
      padding: 15px;
      margin: 15px 0;
      background: #ffffff;
    }
    
    .credential-item {
      margin: 12px 0;
    }
    
    .credential-label {
      font-size: 9pt;
      font-weight: bold;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    
    .credential-value {
      font-size: 14pt;
      font-weight: bold;
      font-family: 'Courier New', 'Courier', monospace;
      border: 1px solid #000000;
      padding: 8px;
      background: #f5f5f5;
      margin-top: 4px;
    }
    
    .instructions {
      margin: 15px 0;
      font-size: 11pt;
    }
    
    .instructions h2 {
      font-size: 12pt;
      font-weight: bold;
      margin: 12px 0 8px 0;
    }
    
    .instructions ol {
      margin: 8px 0;
      padding-left: 20px;
    }
    
    .instructions li {
      margin: 6px 0;
    }
    
    .qr-section {
      text-align: center;
      margin: 15px 0;
      page-break-inside: avoid;
    }
    
    .qr-code {
      width: 120px;
      height: 120px;
      margin: 8px auto;
    }
    
    .qr-label {
      font-size: 10pt;
      font-weight: bold;
      margin-top: 8px;
    }
    
    .website-link {
      font-size: 10pt;
      margin-top: 5px;
      color: #000000;
    }
    
    .footer {
      margin-top: 15px;
      padding-top: 10px;
      border-top: 1px solid #000000;
      text-align: center;
      font-size: 9pt;
    }
    
    @media print {
      body {
        padding: 0;
      }
      
      .plain-header {
        page-break-after: avoid;
      }
    }
  </style>
</head>
<body>
  ${headerHTML}

  <h1>SpellTales Login Credentials</h1>
  
  <div class="intro">
    <strong>Dear Parent/Guardian,</strong><br><br>
    Your child, <strong>${student.firstName} ${student.lastName}</strong>${student.gradeName ? ` (Grade ${student.gradeName})` : ''}, has been registered for SpellTales - an educational app that develops literacy through creative storytelling.
  </div>

  <div class="credentials-box">
    <div class="credential-item">
      <div class="credential-label">Username</div>
      <div class="credential-value">${student.username}</div>
    </div>
    
    <div class="credential-item">
      <div class="credential-label">Password</div>
      <div class="credential-value">${student.password}</div>
    </div>
  </div>

  <div class="instructions">
    <h2>Getting Started</h2>
    <ol>
      <li>Download the iOS app using the QR code below or go to <strong>spelltales.co.uk</strong></li>
      <li>Open the app and select "School Login"</li>
      <li>Enter the username and password shown above</li>
      <li>Your child can start creating stories!</li>
    </ol>
  </div>

  <div class="qr-section">
    <img src="${QR_CODE_IMAGE_PATH}" alt="Download SpellTales" class="qr-code">
    <div class="qr-label">Scan to Download iOS App</div>
    <div class="website-link">or visit spelltales.co.uk</div>
  </div>

  <div class="footer">
    <p>
      ${teacherName} • ${schoolSettings.schoolName}<br>
      Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
    </p>
  </div>
</body>
</html>
  `.trim();
};

/**
 * Generate HTML for multiple students (class credentials)
 */
export const generateClassCredentialsHTML = async (
  students: StudentCredential[],
  teacherName: string,
  schoolSettings: SchoolSettings,
  gradeName: string
): Promise<string> => {
  const useCustomHeader = schoolSettings.useCustomHeader ?? false;
  
  const studentCards = await Promise.all(students.map(async (student) => {
    const headerHTML = useCustomHeader ? `
      <div class="header custom-header">
        ${schoolSettings.schoolLogo ? `<img src="${schoolSettings.schoolLogo}" alt="${schoolSettings.schoolName}" class="logo">` : ''}
        <div class="school-name">${schoolSettings.schoolName}</div>
        ${schoolSettings.schoolAddress || schoolSettings.schoolPhone || schoolSettings.schoolEmail ? `
          <div class="school-details">
            ${schoolSettings.schoolAddress ? `${schoolSettings.schoolAddress} | ` : ''}
            ${schoolSettings.schoolPhone ? `${schoolSettings.schoolPhone} | ` : ''}
            ${schoolSettings.schoolEmail || ''}
          </div>
        ` : ''}
      </div>
    ` : `
      <div class="header plain-header">
        <!-- Space for school letterhead -->
      </div>
    `;
    
    return `
    <div class="student-card">
      ${headerHTML}

      <h1>SpellTales Login Credentials</h1>
      
      <div class="intro">
        <strong>Dear Parent/Guardian,</strong><br><br>
        Your child, <strong>${student.firstName} ${student.lastName}</strong>${student.gradeName ? ` (Grade ${student.gradeName})` : ''}, has been registered for SpellTales - an educational app that develops literacy through creative storytelling.
      </div>

      <div class="credentials-box">
        <div class="credential-item">
          <div class="credential-label">Username</div>
          <div class="credential-value">${student.username}</div>
        </div>
        
        <div class="credential-item">
          <div class="credential-label">Password</div>
          <div class="credential-value">${student.password}</div>
        </div>
      </div>

      <div class="instructions">
        <h2>Getting Started</h2>
        <ol>
          <li>Download the iOS app using the QR code below or go to <strong>spelltales.co.uk</strong></li>
          <li>Open the app and select "School Login"</li>
          <li>Enter the username and password shown above</li>
          <li>Your child can start creating stories!</li>
        </ol>
      </div>

      <div class="qr-section">
        <img src="${QR_CODE_IMAGE_PATH}" alt="Download SpellTales" class="qr-code">
        <div class="qr-label">Scan to Download iOS App</div>
        <div class="website-link">or visit spelltales.co.uk</div>
      </div>

      <div class="footer">
        <p>
          ${teacherName} • ${schoolSettings.schoolName}<br>
          Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>
    </div>
  `;
  }));

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>SpellTales Login Credentials - Grade ${gradeName}</title>
  <style>
    @page {
      size: A4;
      margin: 1.5cm;
    }
    
    body {
      font-family: 'Arial', 'Helvetica', sans-serif;
      line-height: 1.6;
      color: #000000;
      padding: 0;
      font-size: 14pt;
    }
    
    .student-card {
      page-break-after: always;
      page-break-inside: avoid;
      max-width: 800px;
      margin: 0 auto 30px auto;
    }
    
    .student-card:last-child {
      page-break-after: auto;
    }
    
    .header {
      margin-bottom: 20px;
    }
    
    .plain-header {
      height: 4cm;
      border-bottom: none;
    }
    
    .custom-header {
      text-align: center;
      border-bottom: 2px solid #000000;
      padding-bottom: 10px;
      margin-bottom: 15px;
    }
    
    .logo {
      max-width: 150px;
      max-height: 60px;
      margin-bottom: 8px;
    }
    
    .school-name {
      font-size: 16pt;
      font-weight: bold;
      color: #000000;
      margin: 8px 0;
    }
    
    .school-details {
      font-size: 10pt;
      color: #333333;
    }
    
    h1 {
      font-size: 16pt;
      font-weight: bold;
      margin: 0 0 15px 0;
      text-align: center;
    }
    
    .intro {
      margin: 15px 0;
      font-size: 11pt;
      line-height: 1.5;
    }
    
    .credentials-box {
      border: 2px solid #000000;
      padding: 15px;
      margin: 15px 0;
      background: #ffffff;
    }
    
    .credential-item {
      margin: 12px 0;
    }
    
    .credential-label {
      font-size: 9pt;
      font-weight: bold;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    
    .credential-value {
      font-size: 14pt;
      font-weight: bold;
      font-family: 'Courier New', 'Courier', monospace;
      border: 1px solid #000000;
      padding: 8px;
      background: #f5f5f5;
      margin-top: 4px;
    }
    
    .instructions {
      margin: 15px 0;
      font-size: 11pt;
    }
    
    .instructions h2 {
      font-size: 12pt;
      font-weight: bold;
      margin: 12px 0 8px 0;
    }
    
    .instructions ol {
      margin: 8px 0;
      padding-left: 20px;
    }
    
    .instructions li {
      margin: 6px 0;
    }
    
    .qr-section {
      text-align: center;
      margin: 15px 0;
      page-break-inside: avoid;
    }
    
    .qr-code {
      width: 120px;
      height: 120px;
      margin: 8px auto;
    }
    
    .qr-label {
      font-size: 10pt;
      font-weight: bold;
      margin-top: 8px;
    }
    
    .website-link {
      font-size: 10pt;
      margin-top: 5px;
      color: #000000;
    }
    
    .footer {
      margin-top: 15px;
      padding-top: 10px;
      border-top: 1px solid #000000;
      text-align: center;
      font-size: 9pt;
    }
    
    @media print {
      body {
        padding: 0;
      }
      
      .plain-header {
        page-break-after: avoid;
      }
    }
  </style>
</head>
<body>
  ${studentCards.join('\n')}
</body>
</html>
  `.trim();
};

/**
 * Convert HTML to PDF and download
 */
export const downloadPDF = async (html: string, filename: string) => {
  // Create a temporary container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = '210mm'; // A4 width
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    // Wait for images to load with timeout and convert to data URLs
    const images = container.getElementsByTagName('img');
    await Promise.all(
      Array.from(images).map(img => {
        return new Promise((resolve) => {
          const timeout = setTimeout(() => {
            console.warn(`Image failed to load: ${img.src}`);
            resolve(null);
          }, 5000);
          
          const loadAndConvert = () => {
            clearTimeout(timeout);
            // Convert image to data URL to ensure it's embedded
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth || img.width;
              canvas.height = img.naturalHeight || img.height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0);
                img.src = canvas.toDataURL('image/png');
              }
            } catch (e) {
              console.warn('Failed to convert image to data URL:', e);
            }
            resolve(null);
          };
          
          if (img.complete && img.naturalHeight !== 0) {
            loadAndConvert();
          } else {
            img.onload = loadAndConvert;
            img.onerror = () => {
              clearTimeout(timeout);
              console.error(`Image error: ${img.src}`);
              resolve(null);
            };
          }
        });
      })
    );

    // Get all student cards (for class credentials) or use the whole body (for single student)
    const cards = container.querySelectorAll('.student-card');
    const pdf = new jsPDF('p', 'mm', 'a4');
    let isFirstPage = true;

    // If no student cards found, render the whole body (single student credential)
    const elementsToRender = cards.length > 0 ? Array.from(cards) : [container.querySelector('body') || container];

    for (const card of elementsToRender) {
      if (!isFirstPage) {
        pdf.addPage();
      }
      isFirstPage = false;

      // Convert card to canvas
      // Note: html2canvas doesn't support oklch() colors, so we need to convert them first
      const canvas = await html2canvas(card as HTMLElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          // Convert any oklch colors to rgb before rendering
          // We need to get the original element to read computed styles
          const allElements = clonedDoc.querySelectorAll('*');
          const originalElements = (card as HTMLElement).querySelectorAll('*');
          
          allElements.forEach((el, index) => {
            const element = el as HTMLElement;
            const originalElement = originalElements[index] as HTMLElement;
            
            if (!originalElement) return;
            
            try {
              // Get computed color values from original element (browser converts oklch to rgb)
              const computedStyle = window.getComputedStyle(originalElement);
              
              const color = computedStyle.color;
              const bgColor = computedStyle.backgroundColor;
              const borderColor = computedStyle.borderColor;
              const borderTopColor = computedStyle.borderTopColor;
              const borderRightColor = computedStyle.borderRightColor;
              const borderBottomColor = computedStyle.borderBottomColor;
              const borderLeftColor = computedStyle.borderLeftColor;
              
              // Apply the computed rgb values directly to cloned element
              if (color && color !== 'rgba(0, 0, 0, 0)' && !color.includes('oklch')) {
                element.style.color = color;
              }
              if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && !bgColor.includes('oklch')) {
                element.style.backgroundColor = bgColor;
              }
              if (borderColor && borderColor !== 'rgba(0, 0, 0, 0)' && !borderColor.includes('oklch')) {
                element.style.borderColor = borderColor;
              }
              if (borderTopColor && borderTopColor !== 'rgba(0, 0, 0, 0)' && !borderTopColor.includes('oklch')) {
                element.style.borderTopColor = borderTopColor;
              }
              if (borderRightColor && borderRightColor !== 'rgba(0, 0, 0, 0)' && !borderRightColor.includes('oklch')) {
                element.style.borderRightColor = borderRightColor;
              }
              if (borderBottomColor && borderBottomColor !== 'rgba(0, 0, 0, 0)' && !borderBottomColor.includes('oklch')) {
                element.style.borderBottomColor = borderBottomColor;
              }
              if (borderLeftColor && borderLeftColor !== 'rgba(0, 0, 0, 0)' && !borderLeftColor.includes('oklch')) {
                element.style.borderLeftColor = borderLeftColor;
              }
            } catch (e) {
              // Ignore errors for individual elements
              console.warn('Failed to convert colors for element:', e);
            }
          });
        }
      });

      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add image to PDF
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    }

    // Download PDF
    pdf.save(filename);
  } finally {
    // Clean up
    document.body.removeChild(container);
  }
};

/**
 * Download HTML as file (legacy - kept for compatibility)
 */
export const downloadHTML = (html: string, filename: string) => {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Print HTML content
 */
export const printHTML = (html: string) => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  }
};
